import { useEffect, useState } from "react";
import _ from "lodash";

import { Form, Checkbox, Col, Row } from "antd";
import { GKPresetColors } from "../../../MediaLibrary/EditModal/LeftSidebar/EditTab";

export const InputCheckboxGroup = (props) => {
  const { item, form, isCutoutModal, presetBgColor, templateData, setTemplateData, presetGkColor, includeSmiling } = props;

  if (item.data) {
    item.data.sort((a, b) => a.shirtNumber - b.shirtNumber);
  }

  const otherGroupId =
    item.id === "lineup_players"
      ? "reserve_players"
      : item.id === "reserve_players"
        ? "lineup_players"
        : null;

  const [selectedValues, setSelectedValues] = useState(item.defaultValue || []);

  const MAX_SELECTION = 11;
  const SUB_MAX_SELECTION = 15;

  // Split data into two columns
  const midIndex = Math.ceil(item.data?.length / 2);
  const firstColumn = item.data?.slice(0, midIndex);
  const secondColumn = item.data?.slice(midIndex);
  const otherGroupValues = form?.getFieldValue(otherGroupId) || [];

  const getColorKeyForPlayer = (player) => {
    if (player.position === "Goalkeeper") {
      let colorKey = 'red';
      if (presetGkColor === GKPresetColors.black) colorKey = 'black';
      else if (presetGkColor === GKPresetColors.purple) colorKey = 'purple';
      else if (presetGkColor === GKPresetColors.green) colorKey = 'green';
      else if (presetGkColor === GKPresetColors.red) colorKey = 'red';
      return colorKey;
    }

    let colorKey = 'white';
    if (presetBgColor === '#ad042b') colorKey = 'red';
    else if (presetBgColor === '#111111') colorKey = 'black';
    return colorKey;
  };

  // Photo positions are 1-indexed and correspond 1:1 with the uploaded
  // file's own index (see FCBPlayerImage / UploadFCBFiles) — unless smiling
  // photos are excluded, in which case only positions whose type isn't
  // 'smiling' are eligible.
  const buildPositionPool = (player, colorKey) => {
    const lenKey = `${colorKey}Len`;
    const typesKey = `${colorKey}Types`;
    const total = player[lenKey] || 0;
    const types = player[typesKey] || [];
    const all = Array.from({ length: total }, (_, i) => i + 1);
    return includeSmiling ? all : all.filter((pos) => types[pos - 1] !== 'smiling');
  };

  const getNextPos = (player, colorKey) => {
    const posKey = `${colorKey}Pos`;
    const pool = buildPositionPool(player, colorKey);

    // No position satisfies the current filter (e.g. every photo for this
    // jersey is tagged smiling and smiling is excluded) — 0 signals "no
    // eligible photo" rather than falling back to the raw last/first
    // position, which could silently be a smiling one.
    if (pool.length === 0) return 0;

    const currentIndex = pool.indexOf(player[posKey]);
    return pool[(currentIndex + 1) % pool.length];
  };

  // Keeps an already-selected player's position valid whenever the smiling
  // filter itself changes (not just when the player's checkbox is toggled):
  // stay put if still eligible, snap to the first eligible photo if not, or
  // go blank (0) if nothing qualifies at all.
  const getClampedPos = (player, colorKey) => {
    const posKey = `${colorKey}Pos`;
    const pool = buildPositionPool(player, colorKey);

    if (pool.length === 0) return 0;
    if (pool.includes(player[posKey])) return player[posKey];
    return pool[0];
  };

  // Re-evaluate currently-selected players' positions whenever "Include
  // smiling" is flipped, so the displayed photo (or lack of one) reacts
  // immediately instead of only updating the next time someone re-toggles
  // that player's own checkbox.
  useEffect(() => {
    if (!["lineup_players", "reserve_players"].includes(item.id)) return;
    if (!selectedValues.length) return;

    const updatedTemplateData = _.cloneDeep(templateData);
    const fieldIndex = updatedTemplateData?.data?.fields?.findIndex(f => f.id === item.id);
    if (fieldIndex === -1 || fieldIndex === undefined) return;

    let changed = false;
    const allData = _.get(updatedTemplateData, `data.fields[${fieldIndex}].data`, []);
    const updatedData = allData.map(player => {
      if (!selectedValues.includes(player.id)) return player;

      const colorKey = getColorKeyForPlayer(player);
      const posKey = `${colorKey}Pos`;
      const nextPos = getClampedPos(player, colorKey);

      if (nextPos === player[posKey]) return player;
      changed = true;
      return { ...player, [posKey]: nextPos };
    });

    if (!changed) return;

    _.set(updatedTemplateData, `data.fields[${fieldIndex}].data`, updatedData);
    setTemplateData(updatedTemplateData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeSmiling]);

  const handleChange = (checkedValues) => {
    let maxCount = item.id === 'lineup_players' ? MAX_SELECTION : SUB_MAX_SELECTION;
    if (checkedValues.length > maxCount) {
      checkedValues = checkedValues.slice(0, maxCount);
    }

    const prevValues = [...selectedValues];
    const added = checkedValues.find((val) => !prevValues.includes(val));
    const removed = prevValues.find((val) => !checkedValues.includes(val));
    const changedValue = added || removed;
    const allLineUpData = [...item.data];

    const updatedLineUp = allLineUpData.map(player => {
      if (player.id !== changedValue) return player;
      const updatedPlayer = { ...player };
      if (!added) return updatedPlayer;

      const colorKey = getColorKeyForPlayer(updatedPlayer);
      const posKey = `${colorKey}Pos`;
      updatedPlayer[posKey] = getNextPos(player, colorKey);
      return updatedPlayer;
    }).filter(player => player.type !== 'manualPlayer');
    setSelectedValues(checkedValues);

    const updatedTemplateData = _.cloneDeep(templateData);
    const index = updatedTemplateData.data.fields.findIndex(f => f.id === 'lineup_players');
    if (index !== -1) {
      _.set(updatedTemplateData, `data.fields[${index}].data`, updatedLineUp);
    }
    setTemplateData(updatedTemplateData)

    form?.setFieldsValue({
      [item.id]: checkedValues,
    });
  };

  const isDisabled = (playerId) => {
    return otherGroupId && otherGroupValues.includes(playerId);
  };

  useEffect(() => {
    if (["lineup_players", "reserve_players"].includes(item.id)) {
      const currentValues = form.getFieldValue(item.id) || [];
      setSelectedValues(currentValues);
    }
  }, [item.id]);

  return (
    <>
      <Form.Item
        label={!isCutoutModal && item.label}
        name={item?.id}
        rules={[{ required: false, message: "Please input your username!" }]}
        tooltip={props.tooltip}
        style={props?.style}
        initialValue={item.defaultValue || ""}
      >
        <Checkbox.Group
          style={{
            width: "100%",
          }}
          // value={selectedList}
          // onChange={onChange}
          value={selectedValues}
          onChange={handleChange}
        >
          {["lineup_players", "reserve_players"].includes(item.id) ? (
            <Row style={{ display: 'flex'}}> 
              {isCutoutModal ?
                (

                  <>
                    <Col span={12}>
                      {
                        item.data?.slice(0, Math.ceil(item.data.length / 2)).map((element, index) => (
                          <Checkbox
                            key={index}
                            value={element.id}
                            disabled={isDisabled(element.id)}
                          >
                            <div style={{ display: "flex", fontSize: 18 }}>
                              <span
                                style={{
                                  width: 25,
                                  textAlign: "center",
                                  marginRight: 3,
                                }}
                              >
                                {element.shirtNumber}
                              </span>
                              <span
                                style={{
                                  width: 150,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {element.shortLastName}
                              </span>
                            </div>
                          </Checkbox>
                        ))}
                    </Col>

                    <Col span={12}>
                      {
                        item.data?.slice(Math.ceil(item.data.length / 2)).map((element, index) => (
                          <Checkbox
                            key={index}
                            value={element.id}
                            disabled={isDisabled(element.id)}
                          >
                            <div style={{ display: "flex", fontSize: 18 }}>
                              <span
                                style={{
                                  width: 25,
                                  textAlign: "center",
                                  marginRight: 3,
                                }}
                              >
                                {element.shirtNumber}
                              </span>
                              <span
                                style={{
                                  width: 150,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {element.shortLastName}
                              </span>
                            </div>
                          </Checkbox>
                        ))}
                    </Col>
                  </>
                ) : (
                  <>
                    <Col span={12} style={{ paddingBottom: 10 }}>
                      {firstColumn.map((element, index) => (
                        <Checkbox
                          key={index}
                          value={element.id}
                          disabled={isDisabled(element.id)}
                        >
                          <div style={{ display: "flex" }}>
                            <span
                              style={{
                                width: 16,
                                textAlign: "end",
                                marginRight: 3,
                              }}
                            >
                              {element.shirtNumber}
                            </span>
                            <span
                              style={{
                                width: 160,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {element.label}
                            </span>
                          </div>
                        </Checkbox>
                      ))}
                    </Col>

                    <Col span={12}>
                      {secondColumn.map((element, index) => (
                        <Checkbox
                          key={index}
                          value={element.id}
                          disabled={isDisabled(element.id)}
                        >
                          <div style={{ display: "flex" }}>
                            <span
                              style={{
                                width: 16,
                                textAlign: "end",
                                marginRight: 3,
                              }}
                            >
                              {element.shirtNumber}
                            </span>
                            <span
                              style={{
                                width: 160,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {element.label}
                            </span>
                          </div>
                        </Checkbox>
                      ))}
                    </Col>
                  </>
                )}
            </Row>
          ) : (
            <Row>
              {item.data?.map((element, index) => {
                return (
                  <Col span={12} key={index}>
                    <Checkbox value={element.id} onChange={handleChange}>
                      <div style={{ display: "flex" }}>
                        <span
                          style={{
                            display: "inline-block",
                            width: 16,
                            textAlign: "end",
                            marginRight: 3,
                          }}
                        >
                          {element.shirtNumber}
                        </span>
                        <span
                          style={{
                            display: "inline-block",
                            width: 160,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {element.label}
                        </span>
                      </div>
                    </Checkbox>
                  </Col>
                );
              })}
            </Row>
          )}
        </Checkbox.Group>
      </Form.Item>
    </>
  );
};
